import { useEffect, useState } from 'react'

import {
  addPromptTemplate,
  type PromptTemplate,
  removePromptTemplate,
  updatePromptTemplate,
} from '../../lib/promptTemplateStore'
import { ChromePrimaryButton, ChromeToolbarButton } from '../ChromeActions'
import { ChromeComposerTextarea } from '../ChromeComposerTextarea'
import { chromeInputClass, chromeMetaTagClass } from '../ChromeSurface'
import { managementListActionClass } from '../ManagementListModal'
import { ThemeModal } from '../modal'

type Props = {
  open: boolean
  onClose: () => void
  angular: boolean
  templates: PromptTemplate[]
  onRefresh: () => void | Promise<void>
  /** 将选中模板正文追加到生图页提示 */
  onAppendSelectedTemplateIds: (orderedIds: string[]) => void
}

export function PromptTemplatesManageModal({
  open,
  onClose,
  angular,
  templates,
  onRefresh,
  onAppendSelectedTemplateIds,
}: Props) {
  const [formOpen, setFormOpen] = useState(false)
  const [name, setName] = useState('')
  const [style, setStyle] = useState('')
  const [body, setBody] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [selectedInModal, setSelectedInModal] = useState<string[]>([])

  useEffect(() => {
    if (!open) {
      setFormOpen(false)
      setEditingId(null)
      setName('')
      setStyle('')
      setBody('')
      setSelectedInModal([])
    }
  }, [open])

  const sorted = [...templates].sort((a, b) => {
    const s = a.style.localeCompare(b.style, 'zh-CN')
    if (s !== 0) return s
    return a.name.localeCompare(b.name, 'zh-CN')
  })

  const cancelEdit = () => {
    setEditingId(null)
    setName('')
    setStyle('')
    setBody('')
  }

  const closeFormOnly = () => {
    cancelEdit()
    setFormOpen(false)
  }

  const handleManageClose = () => {
    closeFormOnly()
    onClose()
  }

  const submitAddOrEdit = async () => {
    const n = name.trim()
    const b = body.trim()
    if (!n || !b) return
    if (editingId) {
      await updatePromptTemplate(editingId, {
        name: n,
        style: style.trim() || '自定义',
        body: b,
      })
    } else {
      await addPromptTemplate({
        name: n,
        style: style.trim() || '自定义',
        body: b,
      })
    }
    await onRefresh()
    closeFormOnly()
  }

  const toggleModalSelect = (id: string) => {
    setSelectedInModal((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    )
  }

  const startEdit = (t: PromptTemplate) => {
    setEditingId(t.id)
    setName(t.name)
    setStyle(t.style)
    setBody(t.body)
    setFormOpen(true)
  }

  const openCreateForm = () => {
    cancelEdit()
    setFormOpen(true)
  }

  const listCardRound = angular ? 'rounded-none' : 'rounded-xl'
  const listBadgeRound = angular ? 'rounded-none' : 'rounded-md'

  const canSaveForm = name.trim().length > 0 && body.trim().length > 0

  const listFooter = (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span className="text-xs text-ink-soft">已选 {selectedInModal.length} 个</span>
      <ChromePrimaryButton
        density="compact"
        type="button"
        disabled={selectedInModal.length === 0}
        onClick={() => {
          if (selectedInModal.length === 0) return
          onAppendSelectedTemplateIds([...selectedInModal])
          setSelectedInModal([])
        }}
      >
        将选中内容追加到提示词
      </ChromePrimaryButton>
    </div>
  )

  const formFooter = (
    <div className="flex justify-end gap-2">
      <ChromeToolbarButton type="button" onClick={closeFormOnly}>
        取消
      </ChromeToolbarButton>
      <ChromePrimaryButton
        type="button"
        density="compact"
        disabled={!canSaveForm}
        onClick={submitAddOrEdit}
      >
        {editingId ? '保存' : '添加'}
      </ChromePrimaryButton>
    </div>
  )

  return (
    <>
      <ThemeModal
        open={open}
        onClose={handleManageClose}
        title="提示词模板"
        angular={angular}
        size="lg"
        footer={listFooter}
        closeOnEscape={!formOpen}
      >
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs font-semibold text-ink">模板列表</p>
            <ChromeToolbarButton type="button" onClick={openCreateForm}>
              新建模板
            </ChromeToolbarButton>
          </div>
          {sorted.length === 0 ? (
            <p className="typo-lib-empty py-8 text-center text-sm text-ink-soft">
              暂无模板，点击「新建模板」添加
            </p>
          ) : (
            <ul className="space-y-2">
              {sorted.map((t) => {
                const selected = selectedInModal.includes(t.id)
                return (
                  <li
                    key={t.id}
                    className={[
                      'flex flex-col gap-2 border bg-panel p-3 transition-colors sm:flex-row sm:items-center sm:justify-between',
                      listCardRound,
                      selected
                        ? 'border-accent ring-1 ring-accent/30'
                        : 'border-line hover:border-line',
                    ].join(' ')}
                  >
                    <button
                      type="button"
                      onClick={() => toggleModalSelect(t.id)}
                      className="min-w-0 flex-1 cursor-pointer text-left"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={[
                            'typo-lib-hint shrink-0 border border-line bg-surface-muted px-2 py-0.5 font-medium',
                            listBadgeRound,
                          ].join(' ')}
                        >
                          {t.style}
                        </span>
                        <span className="typo-lib-card-title">{t.name}</span>
                        {selected ? (
                          <span className="text-[10px] font-semibold text-accent">已选</span>
                        ) : null}
                      </div>
                      <p className="typo-lib-card-meta mt-1 line-clamp-2 leading-relaxed">
                        {t.body}
                      </p>
                    </button>
                    {t.editable ? (
                      <div className="flex shrink-0 flex-wrap gap-2 sm:justify-end">
                        <button
                          type="button"
                          className={managementListActionClass(angular)}
                          onClick={(e) => {
                            e.stopPropagation()
                            startEdit(t)
                          }}
                        >
                          编辑
                        </button>
                        <button
                          type="button"
                          className={managementListActionClass(angular, 'danger')}
                          onClick={(e) => {
                            e.stopPropagation()
                            if (!window.confirm(`删除模板「${t.name}」？`)) return
                            void removePromptTemplate(t.id).then(async () => {
                              await onRefresh()
                              if (editingId === t.id) cancelEdit()
                              setSelectedInModal((p) => p.filter((x) => x !== t.id))
                            })
                          }}
                        >
                          删除
                        </button>
                      </div>
                    ) : (
                      <span className={chromeMetaTagClass(angular, 'outline', { hint: true })}>系统</span>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </ThemeModal>

      <ThemeModal
        open={open && formOpen}
        onClose={closeFormOnly}
        title={editingId ? '编辑模板' : '新建模板'}
        angular={angular}
        size="md"
        footer={formFooter}
        stackLevel="nested"
        manageBodyScroll={false}
      >
        <div className="space-y-3">
          <p className="text-xs text-ink-soft">
            {editingId ? '修改名称、标签或正文后保存。' : '填写后保存即可在列表中使用该模板。'}
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="block text-[10px] font-medium text-ink-soft sm:text-xs">
              名称
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={chromeInputClass(angular)}
              />
            </label>
            <label className="block text-[10px] font-medium text-ink-soft sm:text-xs">
              风格标签
              <input
                value={style}
                onChange={(e) => setStyle(e.target.value)}
                placeholder="如：产品、插画"
                className={chromeInputClass(angular)}
              />
            </label>
          </div>
          <label className="block text-[10px] font-medium text-ink-soft sm:text-xs">
            正文
            <ChromeComposerTextarea
              angular={angular}
              variant="form"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              className="!mt-1"
              textareaClassName="min-h-[8rem]"
            />
          </label>
        </div>
      </ThemeModal>
    </>
  )
}
